import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Building2, ExternalLink, Eye, EyeOff, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { EntityModal } from "./EntityModal";
import { EmptyState, SkeletonGrid } from "./common";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useConfirm } from "../../components/useConfirm";

/* ======================================================================== */
/* TAB 2 — ENTIDADES                                                         */
/* ======================================================================== */
export function EntitiesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Iter24.d: confirm dialog reutilizable
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/landing/entities");
      setItems(data.items || data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudieron cargar entidades");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingItem(null); setModalOpen(true); };
  const openEdit = (item) => { setEditingItem(item); setModalOpen(true); };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Eliminar entidad",
      description: "¿Eliminar esta entidad del landing? El logo dejará de aparecer en la sección de prueba social.",
      confirmLabel: "Sí, eliminar",
      destructive: true,
      testIdPrefix: "entity-delete-confirm",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/landing/entities/${id}`);
      toast.success("Entidad eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const toggleVisible = async (item) => {
    try {
      await api.patch(`/admin/landing/entities/${item.id}`, { is_visible: !item.is_visible });
      toast.success(item.is_visible ? "Entidad ocultada" : "Entidad visible");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar");
    }
  };

  return (
    <div className="space-y-4" data-testid="entities-tab">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="font-serif text-lg text-gray-900">Entidades</h2>
          <p className="text-sm text-gray-600">
            Logos de entidades que aparecen en el landing como prueba social. Sube el logo en PNG/SVG (máx. 500 KB).
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="btn-add-entity">
          <Plus className="h-4 w-4 mr-1.5" /> Agregar entidad
        </Button>
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Aún no hay entidades configuradas"
          subtitle="Haz clic en 'Agregar entidad' para subir el primer logo."
        />
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="entities-list">
          {items.map((e) => (
            <li key={e.id} data-testid={`entity-${e.id}`}>
              <Card className="border-gray-200 rounded-sm">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    {e.is_visible ? (
                      <Badge className="rounded-sm bg-primary/10 text-primary border border-primary/30">Visible</Badge>
                    ) : (
                      <Badge className="rounded-sm bg-gray-50 text-gray-500 border border-gray-300">Oculto</Badge>
                    )}
                    <div className="flex gap-1">
                      <button onClick={() => toggleVisible(e)} className="p-1.5 text-gray-500 hover:text-primary" title="Mostrar/ocultar" data-testid={`btn-toggle-${e.id}`}>
                        {e.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button onClick={() => openEdit(e)} className="p-1.5 text-gray-500 hover:text-primary" title="Editar" data-testid={`btn-edit-${e.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(e.id)} className="p-1.5 text-gray-500 hover:text-destructive" title="Eliminar" data-testid={`btn-remove-${e.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 h-24 flex items-center justify-center rounded-sm overflow-hidden p-2">
                    {e.logo_b64 ? (
                      <img src={e.logo_b64} alt={e.name} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <span className="text-xs text-gray-400">(sin logo)</span>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-sm text-gray-900 truncate">{e.name}</div>
                    {e.url && (
                      <a
                        href={e.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                      >
                        {e.url} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400 pt-1 border-t border-gray-100">
                    orden: {e.sort_order}
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {modalOpen && (
        <EntityModal
          item={editingItem}
          onClose={() => { setModalOpen(false); setEditingItem(null); }}
          onSaved={() => { setModalOpen(false); setEditingItem(null); load(); }}
        />
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
